import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

// ---------------------------------------------------------------------------
// Lo que el backend devuelve. Los tipos son un espejo del módulo `bonos` de la
// API y no una versión cómoda: los montos viajan como texto porque un
// DECIMAL(28,8) no entra en un `number` sin perder dígitos, y un bono en
// bolívares es exactamente ese caso. Acá se muestran, no se suman.
// ---------------------------------------------------------------------------

/** Un alcance del catálogo con las monedas que admite. */
export interface AlcanceOpcion {
  alcance: string;
  nombre: string;
  /** USDT siempre, más la moneda local si ese alcance tiene una. */
  monedas: string[];
}

/**
 * Lo que el formulario puede ofrecer.
 *
 * Sale del catálogo del backend a propósito: el formulario se arma con esto y
 * así no puede ni proponer una combinación imposible — CLP para Venezuela no
 * es una advertencia, es una opción que no aparece. Y cuando entre un país
 * nuevo, este formulario ya lo sabe sin tocar una línea.
 */
export interface OpcionesCampana {
  alcances: AlcanceOpcion[];
  /** Monedas en las que ya hay camino para pagar. Ver `sinCamino()`. */
  monedasAcreditables: string[];
  publicos: string[];
  condiciones: string[];
}

export type CampanaEstado =
  | 'BORRADOR'
  | 'PROGRAMADA'
  | 'ACTIVA'
  | 'PAUSADA'
  | 'CERRADA';

export type CampanaPausaMotivo = 'VELOCIDAD' | 'MANUAL';

export interface Campana {
  bonoCampanaId: number;
  nombre: string;
  codigo: string;
  estado: CampanaEstado;
  alcanceCodigo: string;
  alcancePublico: 'TODOS' | 'SIN_ENVIOS' | 'INACTIVO';
  inactivoDias: number | null;
  moneda: string;
  monto: string;
  unidadesTotal: number;
  unidadesUsadas: number;
  /** `unidadesTotal * monto`, calculado por la base. */
  presupuesto: string;
  /** Lo tomado que sigue en pie: reservas vivas más lo acreditado. */
  comprometido: string;
  /** Sólo lo que ya salió. */
  pagado: string;
  vigenciaDesde: string;
  vigenciaHasta: string;
  reservaDias: number;
  condicionAcreditacion: 'AL_RECLAMAR' | 'AL_FONDEAR' | 'AL_PRIMER_ENVIO';
  pausaPct: number | null;
  pausaMinutos: number | null;
  pausadaAt: string | null;
  pausadaMotivo: CampanaPausaMotivo | null;
  /**
   * Quién firmó, como identidad de Cognito.
   *
   * Es el `sub` y no un `usuarioId`: los operadores viven en Cognito y no
   * tienen fila en `user`. El correo viene al lado porque un UUID no le dice
   * nada a quien mira el panel.
   */
  creadaPor: string;
  creadaPorEmail: string | null;
  activadaPor: string | null;
  activadaPorEmail: string | null;
  activadaAt: string | null;
  reanudadaPor: string | null;
  reanudadaPorEmail: string | null;
}

/** El cuerpo para crear. Los montos van como texto, igual que en la API. */
export interface NuevaCampana {
  nombre: string;
  codigo: string;
  alcanceCodigo: string;
  alcancePublico: string;
  inactivoDias?: number;
  moneda: string;
  monto: string;
  unidadesTotal: number;
  vigenciaDesde: string;
  vigenciaHasta: string;
  reservaDias?: number;
  condicionAcreditacion?: string;
  pausaPct?: number;
  pausaMinutos?: number;
}

export interface RitmoCampana {
  tomadosEnVentana: number;
  /** `null` si la campaña no tiene freno configurado. */
  umbral: number | null;
  ventanaMinutos: number | null;
}

export interface TableroResumen {
  unidades: { usadas: number; total: number };
  comprometido: string;
  pagado: string;
  moneda: string;
  ritmo: RitmoCampana;
  rechazos: Array<{ motivo: string; veces: number }>;
  senalesPorRevisar: number;
}

/** Una fila del tablero: un intento, con lo que se sepa de su reclamo. */
export interface FilaTablero {
  fecha: string;
  quien: { nombre: string | null; usuarioId: number | null };
  /** El documento llega **ya enmascarado** del backend. Ver `BonosService`. */
  documento: { tipo: string | null; enmascarado: string | null };
  resultado: string;
  motivo: string | null;
  estado: string | null;
  moneda: string | null;
  monto: string | null;
  expiraAt: string | null;
  senales: string[];
}

export interface GastoPorMoneda {
  moneda: string;
  campanas: number;
  comprometido: string;
  pagado: string;
  pendiente: string;
  bonosAcreditados: number;
  bonosRetenidos: number;
}

export interface KpiCampana {
  codigo: string;
  alcance: string;
  publico: string;
  moneda: string;
  reclamos: number;
  acreditados: number;
  activados: number;
  /** `null` —no 0— cuando la campaña todavía no acreditó nada. */
  tasaActivacion: number | null;
  costoPorPrimerEnvio: string | null;
  reactivados: number;
  pagado: string;
}

export interface ConfigBonos {
  activo: boolean;
  /** Un techo por moneda. **En 0 nadie cobra en esa moneda.** */
  topeMensual: Record<string, number>;
  /**
   * Desde qué bolsa hace falta un segundo operador, por moneda.
   * **En 0 toda campaña de esa moneda necesita dos firmas** — no es «apagado».
   */
  dobleControl: Record<string, number>;
  ritmoIpMax: number;
  ritmoIpMinutos: number;
  ritmoDocMax: number;
  ritmoDocMinutos: number;
  intentoRetencionDias: number;
  pausaPct: number;
  pausaMinutos: number;
  inactivoDias: number;
  fondeoMinClp: number;
  fondeoMinVes: number;
  fondeoMinUsdt: number;
}

/**
 * El módulo de bonos, del lado del operador y del cliente.
 *
 * La ruta de operador es `bonos-control` y **no** `admin/bonos`: en la API esa
 * raíz está tomada por otro enrutamiento y una ruta de operador que empieza
 * con `admin` se pierde. No es una preferencia de nombres, es un error real —
 * referidos tiene un fix por exactamente eso.
 *
 * Este servicio no valida reglas de negocio ni las repite: la moneda que
 * corresponde a cada alcance, el umbral del doble control y los techos de
 * gasto los decide el backend. Acá se pide qué se puede elegir y se muestra lo
 * que contesta. Duplicar una regla de plata en el front es garantizar que
 * dentro de seis meses las dos versiones no digan lo mismo.
 */
@Injectable({ providedIn: 'root' })
export class BonosService {
  private baseUrl = environment.apiNest;

  constructor(private http: HttpClient) {}

  /**
   * Las consultas públicas mandan el header `skip`, que es como el resto del
   * repo evita el interceptor de sesión: sin eso, una consulta sin sesión
   * dispararía el flujo de «sesión vencida».
   */
  private publicHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', skip: 'true' });
  }

  // ------------------------------------------------------------- operador

  /** Qué puede elegir el formulario. Sale del catálogo, no de una constante. */
  async opciones(): Promise<OpcionesCampana> {
    return firstValueFrom(
      this.http.get<OpcionesCampana>(`${this.baseUrl}bonos-control/opciones`)
    );
  }

  async listarCampanas(): Promise<Campana[]> {
    const r = await firstValueFrom(
      this.http.get<{ items: Campana[] }>(`${this.baseUrl}bonos-control/campanas`)
    );
    return r.items ?? [];
  }

  async verCampana(id: number): Promise<Campana> {
    return firstValueFrom(
      this.http.get<Campana>(`${this.baseUrl}bonos-control/campanas/${id}`)
    );
  }

  /** Nace en BORRADOR: crear no habilita nada todavía. */
  async crearCampana(cuerpo: NuevaCampana): Promise<Campana> {
    return firstValueFrom(
      this.http.post<Campana>(`${this.baseUrl}bonos-control/campanas`, cuerpo)
    );
  }

  /**
   * Activar o programar. Puede volver 403 y eso **no es un error de permisos**:
   * es el doble control diciendo que le toca a otro operador. La pantalla lo
   * distingue por el estado y muestra a quién le falta firmar.
   */
  async activar(id: number): Promise<Campana> {
    return firstValueFrom(
      this.http.put<Campana>(`${this.baseUrl}bonos-control/campanas/${id}/activar`, {})
    );
  }

  /** Frenar nunca es el riesgo: no pide segundo operador. */
  async pausar(id: number): Promise<Campana> {
    return firstValueFrom(
      this.http.put<Campana>(`${this.baseUrl}bonos-control/campanas/${id}/pausar`, {})
    );
  }

  /** Si la pausó la velocidad, exige segundo operador igual que activar. */
  async reanudar(id: number): Promise<Campana> {
    return firstValueFrom(
      this.http.put<Campana>(`${this.baseUrl}bonos-control/campanas/${id}/reanudar`, {})
    );
  }

  async cerrar(id: number): Promise<Campana> {
    return firstValueFrom(
      this.http.put<Campana>(`${this.baseUrl}bonos-control/campanas/${id}/cerrar`, {})
    );
  }

  async tablero(id: number): Promise<TableroResumen> {
    return firstValueFrom(
      this.http.get<TableroResumen>(`${this.baseUrl}bonos-control/campanas/${id}/tablero`)
    );
  }

  /**
   * Los reclamos de una campaña. **Los rechazos vienen por default.**
   *
   * No es un detalle de la API: un tablero que sólo muestra quién cobró deja
   * invisible al que probó veinte veces, y el ataque se ve en los rechazos.
   */
  async reclamos(
    id: number,
    opciones: { page?: number; limit?: number; incluirRechazos?: boolean } = {}
  ): Promise<{ items: FilaTablero[]; page: number; limit: number; total: number }> {
    const params = new URLSearchParams();
    params.set('page', String(opciones.page ?? 1));
    params.set('limit', String(opciones.limit ?? 50));
    if (opciones.incluirRechazos === false) params.set('incluirRechazos', 'false');

    return firstValueFrom(
      this.http.get<{
        items: FilaTablero[];
        page: number;
        limit: number;
        total: number;
      }>(`${this.baseUrl}bonos-control/campanas/${id}/reclamos?${params.toString()}`)
    );
  }

  /**
   * El gasto por moneda, sin consolidar en dólares.
   *
   * El rango filtra por la fecha del **reclamo** y no de la acreditación: un
   * bono reclamado en septiembre que se acredita en octubre es plata que se
   * prometió en septiembre.
   */
  async gasto(rango: { desde?: string; hasta?: string } = {}): Promise<GastoPorMoneda[]> {
    const params = new URLSearchParams();
    if (rango.desde) params.set('desde', rango.desde);
    if (rango.hasta) params.set('hasta', rango.hasta);
    const cola = params.toString() ? `?${params.toString()}` : '';

    const r = await firstValueFrom(
      this.http.get<{ items: GastoPorMoneda[] }>(
        `${this.baseUrl}bonos-control/reportes/gasto${cola}`
      )
    );
    return r.items ?? [];
  }

  async kpi(): Promise<KpiCampana[]> {
    const r = await firstValueFrom(
      this.http.get<{ items: KpiCampana[] }>(`${this.baseUrl}bonos-control/reportes/kpi`)
    );
    return r.items ?? [];
  }

  /** La configuración vigente del módulo. */
  async config(): Promise<ConfigBonos> {
    return firstValueFrom(
      this.http.get<ConfigBonos>(`${this.baseUrl}bonos-control/config`)
    );
  }

  /**
   * Enciende, apaga y cambia los techos de gasto. Devuelve qué cambió.
   *
   * Se manda **sólo lo que el operador tocó**: así dos personas editando cosas
   * distintas no se pisan, y no se reescribe un techo con el valor que este
   * navegador leyó hace veinte minutos.
   *
   * El umbral de doble control viaja como los techos: un mapa por moneda, y
   * sólo las monedas que se tocaron. Subirlo debilita el control, así que el
   * backend lo marca distinto en el canal de Telegram.
   */
  async guardarConfig(
    cambios: Partial<ConfigBonos>
  ): Promise<{ cambios: Array<{ clave: string; antes: string; ahora: string }> }> {
    return firstValueFrom(
      this.http.put<{ cambios: Array<{ clave: string; antes: string; ahora: string }> }>(
        `${this.baseUrl}bonos-control/config`,
        cambios
      )
    );
  }

  // -------------------------------------------------------------- cliente

  /**
   * ¿Este código vale, y cuántos bonos quedan?
   *
   * Es público —no pide sesión— y existe para que la pantalla pueda decir
   * «quedan 37 de 100» **antes** de que el cliente apriete reclamar: con el
   * número a la vista, nadie siente que perdió el tiempo para que al final le
   * digan que se agotó.
   *
   * No lanza nunca. Un código que no existe, uno en borrador y uno que ya
   * terminó devuelven todos `vale: false`, y un problema de red también: la
   * pantalla tiene que poder seguir hasta el botón de reclamar, que es donde
   * el backend decide de verdad. Que un código valga **no** significa que
   * quien pregunta pueda cobrarlo — eso lo deciden los trece chequeos.
   */
  async disponibilidad(codigo: string): Promise<{
    vale: boolean;
    unidadesRestantes: number | null;
    moneda: string | null;
    monto: string | null;
  }> {
    const vacio = { vale: false, unidadesRestantes: null, moneda: null, monto: null };
    const limpio = this.normalizarCodigo(codigo);
    if (limpio.length < 4) return vacio;

    try {
      return await firstValueFrom(
        this.http.post<{
          vale: boolean;
          unidadesRestantes: number | null;
          moneda: string | null;
          monto: string | null;
        }>(
          `${this.baseUrl}public/bonos/disponibilidad`,
          { codigo: limpio },
          { headers: this.publicHeaders() }
        )
      );
    } catch {
      return vacio;
    }
  }

  /**
   * Reclamar un bono con el código que salió en redes.
   *
   * **Nunca lanza por un rechazo.** El backend responde 200 con el motivo
   * porque «este bono se agotó» no es un error de la petición, es el
   * resultado; y un 4xx haría que el cliente HTTP lo trate como fallo. El
   * mensaje ya viene redactado para el cliente: no se reescribe acá.
   */
  async reclamar(codigo: string): Promise<{
    ok: boolean;
    motivo?: string;
    mensaje?: string;
    bono?: { id: number; estado: string; moneda: string; monto: string; expiraAt: string | null };
  }> {
    return firstValueFrom(
      this.http.post<any>(`${this.baseUrl}bonos/reclamar`, { codigo })
    );
  }

  async misBonos(): Promise<any[]> {
    const r = await firstValueFrom(
      this.http.get<{ items: any[] }>(`${this.baseUrl}bonos/mis-bonos`)
    );
    return r.items ?? [];
  }

  // ---------------------------------------------------------------- reglas

  /**
   * Normaliza el código como el backend: sin espacios, en mayúscula.
   *
   * Se hace igual en los dos lados por una razón concreta: el operador tiene
   * que ver en el campo el código exacto que va a la pieza gráfica, no uno que
   * el servidor convirtió después de guardar.
   */
  normalizarCodigo(entrada: string): string {
    return (entrada ?? '').trim().replace(/\s+/g, '').toUpperCase().slice(0, 24);
  }

  /** Valida la forma del código en el cliente, para no ir al servidor de más. */
  validarCodigo(entrada: string): { valido: boolean; mensaje?: string } {
    const codigo = this.normalizarCodigo(entrada);
    if (!codigo) return { valido: false, mensaje: 'La campaña necesita un código.' };
    if (codigo.length < 4) {
      return { valido: false, mensaje: 'Un código de menos de 4 caracteres se adivina.' };
    }
    if (!/^[A-Z0-9]+$/.test(codigo)) {
      return {
        valido: false,
        mensaje: 'Sólo letras y números: un código con guiones o tildes se tipea mal.',
      };
    }
    return { valido: true };
  }

  /**
   * Un monto como lo lee una persona: `5`, `5,5`, `20.000`.
   *
   * El backend manda `5.00000000` porque la columna es `DECIMAL(28,8)` y esos
   * decimales existen para que las cuentas cierren, no para mostrarse. Ocho
   * ceros en pantalla no informan nada: hacen que un monto de cinco dólares
   * parezca un número de serie.
   *
   * **Es sólo para mostrar.** Nunca para calcular ni para mandar al backend —
   * ahí los montos siguen viajando como texto exacto. Redondear un valor y
   * después operarlo es cómo aparecen los descuadres de un centavo.
   */
  montoLegible(monto: string | number | null | undefined): string {
    if (monto === null || monto === undefined || monto === '') return '';
    const n = Number(monto);
    if (!Number.isFinite(n)) return String(monto);

    // Sin decimales si es redondo: «20.000» y no «20.000,00».
    const decimales = Number.isInteger(n) ? 0 : 2;
    return n.toLocaleString('es-CL', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  }

  /**
   * Cuántos decimales admite una moneda. Es lo mismo que valida el catálogo.
   *
   * Sirve para avisar en el formulario antes de mandar: un bono de 10.000,50
   * pesos lo rechaza el backend, y mejor decirlo mientras se escribe.
   */
  decimalesDe(moneda: string): number {
    return moneda === 'USDT' ? 8 : 2;
  }

  /**
   * `true` si esa moneda todavía no tiene camino para pagar.
   *
   * Hoy es el caso de CLP: la campaña se puede crear y programar, pero sus
   * reclamos se van a rechazar hasta que exista el asiento. Decirlo antes de
   * que alguien arme la campaña entera es más barato que descubrirlo con el
   * primer cliente adentro.
   */
  sinCamino(moneda: string, opciones: OpcionesCampana | null): boolean {
    if (!opciones || !moneda) return false;
    return !opciones.monedasAcreditables.includes(moneda);
  }

  /**
   * La bolsa: unidades por monto, en la moneda de la campaña.
   *
   * Se calcula con entero escalado y no con `number` a secas porque este
   * número es el que el operador mira antes de firmar: `0.1 * 3` en punto
   * flotante da 0.30000000000000004, y una bolsa que se muestra así no la
   * firma nadie — con razón.
   */
  bolsa(monto: string, unidades: number, moneda: string): string | null {
    const decimales = this.decimalesDe(moneda);
    const limpio = (monto ?? '').trim();
    if (!limpio || !/^\d+(\.\d+)?$/.test(limpio)) return null;
    if (!Number.isInteger(unidades) || unidades < 1) return null;

    const [entera, decimal = ''] = limpio.split('.');
    if (decimal.length > decimales) return null;

    const escalado = BigInt(entera + decimal.padEnd(decimales, '0'));
    const total = (escalado * BigInt(unidades)).toString().padStart(decimales + 1, '0');

    const corte = total.length - decimales;
    const parteEntera = total.slice(0, corte);
    const parteDecimal = total.slice(corte).replace(/0+$/, '');
    return parteDecimal ? `${parteEntera}.${parteDecimal}` : parteEntera;
  }
}
